import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";
import { PtrsActions, TransactionFormActions } from "../actions";
import { ServiceError } from "../models/shared/ServiceError";
import { Store } from "@ngrx/store";
import { selectIsFromEdit } from "../reducers";
import { PtrsService } from "../services/ptrs.service";
import { PtrsResponse } from "../models/ptrs/PtrsApiResponse";
import { TransInquiryResponse } from "../../transaction-view/models/TransInquiryResponse";
import { TransInquiryTrustFields } from "../../transaction-view/models/TransInquiryTrustFields";
import { PtrsTransaction } from "../models/internal-transfer/InternalTransferInquiryResponse";
import { paidForContact } from "../models/ptrs/PaidForContact";
import { PtrsFields } from "../models/check/CheckInquiryResponse";
import { concatLatestFrom } from "@ngrx/operators";

@Injectable()
export class PtrsEffects {
    constructor(
        private actions$: Actions,
        private ptrsService: PtrsService,
        private store: Store
    ) { }

    callPtrs$ = createEffect(() =>
        this.actions$.pipe(
            ofType(PtrsActions.callPtrs),
            concatLatestFrom(() => [
                this.store.select(selectIsFromEdit)
            ]),
            switchMap(([action, isFromEdit]) => {
                if (isFromEdit) {
                    return of(TransactionFormActions.editLoading());
                }
                return this.ptrsService.callPtrs(action.accountId).pipe(
                    map((ptrsResponse: PtrsResponse) => {
                        const ptrsInfo = this.mapApiResponseToPtrsInfo(ptrsResponse)
                        return PtrsActions.callPtrsSuccess(ptrsInfo);
                    }),
                    catchError((err: ServiceError) =>
                        of(PtrsActions.callPtrsFailure(err))
                    )
                )
            })
        )
    );

    mapApiResponseToPtrsInfo(ptrsResponse: PtrsResponse): PtrsResponse {
        return {
            balances: ptrsResponse.balances,
            overdraftRules: ptrsResponse.overdraftRules,
            paidFor: ptrsResponse.paidFor
        }
    }
}

export function mapApiResponseToTransInquiryInfo(transInquiryResponse: TransInquiryResponse[] | PtrsTransaction[] | PtrsFields[]): TransInquiryTrustFields {
    let trustFields = new TransInquiryTrustFields();
    for (let i = 0; i < transInquiryResponse.length; i++) {
        if (transInquiryResponse[i].transactionCode === 'CASHDEP') {
            trustFields.trustIncomeToPercent = transInquiryResponse[i].incomePercent?.toString();
            trustFields.trustPrincipalToPercent = transInquiryResponse[i].principalPercent?.toString();
            trustFields.trustDepositCode = transInquiryResponse[i].taxCode?.toString();
            trustFields.trustComment = transInquiryResponse[i].comment?.toString();
        } else {
            trustFields.trustTaxCode = transInquiryResponse[i].taxCode?.toString();
            if (transInquiryResponse[i].transactionCode === 'JNLDSB') {
                trustFields.trustComment = transInquiryResponse[i].comment?.toString();
            }
            else{
                trustFields.trustComment = trustFields?.trustComment ?? transInquiryResponse[i].comment?.toString();
            }
            trustFields.trustIncomePercent = transInquiryResponse[i].incomePercent?.toString();
            trustFields.trustPrincipalPercent = transInquiryResponse[i].principalPercent?.toString();
            if (transInquiryResponse[i].paidForContact !== null && transInquiryResponse[i].paidForContact.name !== null && transInquiryResponse[i].paidForContact.name !== '') {
                trustFields.trustPaidFor = `${transInquiryResponse[i].paidForContact?.name} - ${transInquiryResponse[i].paidForContact?.role}`
            } 
            else {
                trustFields.trustPaidFor = 'Paid For The Trust - Owner';
            }
            // TODO: don't currently get the trust tax category returned from transInquiry, will need additional work to get this value
            trustFields.trustTaxCategory = '';
        }
    }
    return trustFields;
}
